import { ChevronDown } from '@sortlist-frontend/design-system/icons';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatNumber } from '@sortlist-frontend/utils';

import { rankingConfig } from '../ranking.config';
import type { RankingQuadrant, RankingScatterChart, RankingScatterItem } from '../types';

interface ScatterChartProps {
  chart: RankingScatterChart;
  locale: string;
}

const WIDTH = 640;
const HEIGHT = 420;
const PADDING = { top: 24, right: 24, bottom: 48, left: 56 };
const MIN_RADIUS = 10;
const MAX_RADIUS = 24;

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const scaleX = (value: number, min: number, max: number) => {
  const range = Math.log10(max) - Math.log10(min);
  const span = range === 0 ? 1 : range;
  return PADDING.left + ((Math.log10(value) - Math.log10(min)) / span) * (WIDTH - PADDING.left - PADDING.right);
};

const scaleY = (value: number) => HEIGHT - PADDING.bottom - (value / 100) * (HEIGHT - PADDING.top - PADDING.bottom);

const radius = (size: number, maxSize: number) =>
  MIN_RADIUS + Math.sqrt(size / Math.max(maxSize, 1)) * (MAX_RADIUS - MIN_RADIUS);

const quadrantOf = (item: RankingScatterItem): RankingQuadrant => item.quadrant;

const isRightQuadrant = (item: RankingScatterItem) => item.quadrant === 'leaders' || item.quadrant === 'proven';
const isUpperQuadrant = (item: RankingScatterItem) => item.quadrant === 'leaders' || item.quadrant === 'specialists';

const splitBetween = (
  items: readonly RankingScatterItem[],
  pick: (item: RankingScatterItem) => number,
  isHigh: (item: RankingScatterItem) => boolean,
  step: number,
) => {
  const high = items.filter(isHigh).map(pick);
  const low = items.filter((item) => !isHigh(item)).map(pick);
  if (high.length === 0 && low.length === 0) return 0;
  if (low.length === 0) return Math.min(...high) - step;
  if (high.length === 0) return Math.max(...low) + step;
  const boundary = (Math.max(...low) + Math.min(...high)) / 2;
  return Math.min(...high) > Math.max(...low) ? boundary : median(items.map(pick));
};

const spreadOverlaps = (items: readonly RankingScatterItem[]) => {
  const groups = new Map<string, number[]>();
  items.forEach((item, index) => {
    const key = `${item.x}|${item.y}`;
    groups.set(key, [...(groups.get(key) ?? []), index]);
  });
  const offsets = new Array<number>(items.length).fill(0);
  groups.forEach((indexes) =>
    indexes.forEach((itemIndex, position) => {
      offsets[itemIndex] = (position - (indexes.length - 1) / 2) * MIN_RADIUS * 1.6;
    }),
  );
  return offsets;
};

export const ScatterChart = (props: ScatterChartProps) => {
  const { chart, locale } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);
  const xs = chart.items.map((item) => item.x);
  const minX = Math.max(Math.min(...xs, 1), 1);
  const maxX = Math.max(...xs, minX * 10);
  const maxSize = Math.max(...chart.items.map((item) => item.size), 1);
  const medianX = scaleX(
    Math.min(
      Math.max(
        splitBetween(chart.items, (item) => item.x, isRightQuadrant, 0.5),
        minX,
      ),
      maxX,
    ),
    minX,
    maxX,
  );
  const medianY = scaleY(
    Math.min(
      Math.max(
        splitBetween(chart.items, (item) => item.y, isUpperQuadrant, 1),
        0,
      ),
      100,
    ),
  );
  const overlapOffsets = spreadOverlaps(chart.items);
  const xLabel = chart.axes?.x ?? t('longtail:ranking.scatter.xAxis');
  const yLabel = chart.axes?.y ?? t('longtail:ranking.scatter.yAxis');
  const quadrantLabel = (quadrant: RankingQuadrant) => {
    if (quadrant === 'leaders') return t('longtail:ranking.scatter.quadrants.leaders');
    if (quadrant === 'proven') return t('longtail:ranking.scatter.quadrants.proven');
    if (quadrant === 'specialists') return t('longtail:ranking.scatter.quadrants.specialists');
    return t('longtail:ranking.scatter.quadrants.emerging');
  };

  return (
    <figure className="m-0">
      <figcaption>
        <p className="font-semibold text-secondary-950">{chart.title}</p>
        <p className="mt-1 text-sm text-secondary-500">{chart.description}</p>
      </figcaption>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={chart.title}
        className="mt-6 h-auto w-full text-secondary-700">
        <line
          x1={PADDING.left}
          y1={HEIGHT - PADDING.bottom}
          x2={WIDTH - PADDING.right}
          y2={HEIGHT - PADDING.bottom}
          className="stroke-secondary-300"
        />
        <line
          x1={PADDING.left}
          y1={PADDING.top}
          x2={PADDING.left}
          y2={HEIGHT - PADDING.bottom}
          className="stroke-secondary-300"
        />
        <line
          x1={medianX}
          y1={PADDING.top}
          x2={medianX}
          y2={HEIGHT - PADDING.bottom}
          strokeDasharray="6 6"
          className="stroke-secondary-300"
        />
        <line
          x1={PADDING.left}
          y1={medianY}
          x2={WIDTH - PADDING.right}
          y2={medianY}
          strokeDasharray="6 6"
          className="stroke-secondary-300"
        />
        <text x={WIDTH - PADDING.right} y={PADDING.top + 14} textAnchor="end" className="fill-secondary-500 text-xs">
          {quadrantLabel('leaders')}
        </text>
        <text x={PADDING.left + 8} y={PADDING.top + 14} className="fill-secondary-500 text-xs">
          {quadrantLabel('specialists')}
        </text>
        <text
          x={WIDTH - PADDING.right}
          y={HEIGHT - PADDING.bottom - 8}
          textAnchor="end"
          className="fill-secondary-500 text-xs">
          {quadrantLabel('proven')}
        </text>
        <text x={PADDING.left + 8} y={HEIGHT - PADDING.bottom - 8} className="fill-secondary-500 text-xs">
          {quadrantLabel('emerging')}
        </text>
        <text
          x={(PADDING.left + WIDTH - PADDING.right) / 2}
          y={HEIGHT - 12}
          textAnchor="middle"
          className="fill-secondary-700 text-sm">
          {xLabel}
        </text>
        <text
          x={16}
          y={(PADDING.top + HEIGHT - PADDING.bottom) / 2}
          textAnchor="middle"
          transform={`rotate(-90 16 ${(PADDING.top + HEIGHT - PADDING.bottom) / 2})`}
          className="fill-secondary-700 text-sm">
          {yLabel}
        </text>
        {chart.items.map((item, index) => {
          const cx = scaleX(Math.max(item.x, 1), minX, maxX) + overlapOffsets[index];
          const cy = scaleY(item.y);
          const r = radius(item.size, maxSize);

          return (
            <g key={item.label}>
              <circle cx={cx} cy={cy} r={r} className="fill-primary-500/80 stroke-white" strokeWidth={2}>
                <title>{`${index + 1}. ${item.label} · ${quadrantLabel(quadrantOf(item))}`}</title>
              </circle>
              <text x={cx} y={cy + 4} textAnchor="middle" className="fill-white text-xs font-semibold">
                {index + 1}
              </text>
            </g>
          );
        })}
      </svg>
      <details className="group mt-3 text-sm">
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-medium text-primary-600 [&::-webkit-details-marker]:hidden">
          <ChevronDown size={16} className="transition-transform group-open:rotate-180" aria-hidden="true" />
          {t('longtail:ranking.chart.showData')}
        </summary>
        <table className="mt-3 w-full text-left text-secondary-700">
          <thead>
            <tr>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {t('longtail:ranking.table.agency')}
              </th>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {t('longtail:ranking.scatter.quadrant')}
              </th>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {xLabel}
              </th>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {yLabel}
              </th>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {t('longtail:ranking.scatter.team')}
              </th>
              <th scope="col" className="py-1 font-semibold text-secondary-950">
                {chart.valueLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {chart.items.map((item, index) => (
              <tr key={item.label} className="border-t border-secondary-200">
                <td className="py-1 pr-4">
                  {index + 1}. {item.label}
                </td>
                <td className="py-1 pr-4">{quadrantLabel(quadrantOf(item))}</td>
                <td className="py-1 pr-4">{formatNumber({ value: item.x, locale })}</td>
                <td className="py-1 pr-4">{formatNumber({ value: item.y, locale })}%</td>
                <td className="py-1 pr-4">{formatNumber({ value: item.size, locale })}</td>
                <td className="py-1">{formatNumber({ value: item.value, locale })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
};
